<template>
  <div
    :class="bem({
      [`theme-${theme}`]: true,
      [`block-align-${content.blockAlign}`]: true,
      [`mobile-block-align-${content.mobileBlockAlign}`]: true
    })"
  >
    <StructuredText
      :class="bem('content', {
        [`text-${content.textColour}`]: true,
        [`text-${content.textAlign}`]: true,
        [`mobile-text-${content.mobileTextAlign}`]: true
      })"
      :data="content.copy"
      :render-block="renderBlock"
      :render-link-to-record="renderLinkToRecord"
      :custom-rules="customRules"
    />
  </div>
</template>

<script>
import { StructuredText, renderRule } from 'vue-datocms'
import { isParagraph, isLink, isHeading } from 'datocms-structured-text-utils'
import {
  VisionLink,
  SingleImage,
  HighlightHeading,
  VisionButton,
  TitleBlock,
  ArticleVideo
} from '#components'

export default {
  name: 'GenericStructuredText',

  components: {
    StructuredText,
  },

  props: {
    content: {
      type: Object,
      required: true,
    },
    copySize: {
      type: String,
      default: 'body',
      validator: (value) => ['body-lg', 'body', 'body-sm'].includes(value),
    },
    maxHeadingLevel: {
      type: Number,
      default: 2,
      validator: (value) => [1, 2, 3, 4, 5, 6].includes(value)
    },
    ctaShape: {
      type: String,
      default: 'rounded',
    },
    ctaAlign: {
      type: String,
      default: 'center',
    },
    ctaBaseCopySize: {
      type: String,
      default: 'h4',
    },
    ctaGrow: {
      type: Boolean,
      default: false,
    },
    ctaSize: {
      type: String,
      default: 'small'
    },
    hasParentWrappingLink: {
      type: Boolean,
      default: false
    },
    theme: {
      type: String,
      default: 'default'
    }
  },

  emits: [ 'button-clicked' ],

  setup() {
    const { resolveLocalePath } = useUrlBuilder()

    const sidebarStore = useSidebarStore()
    const modalStore = useModalStore()

    function openSidebarPage(slug) {
      sidebarStore.setActiveSidebar(slug)
    }

    function openModal(slug) {
      modalStore.OPEN_MODAL_PAGE(slug)
    }

    const bem = useBEM('GenericStructuredText')

    return {
      resolveLocalePath,
      openSidebarPage,
      openModal,
      bem
    }
  },

  computed: {
    customRules() {
      return [
        renderRule(
          isHeading,
          ({ adapter: { renderNode: h }, node, children, key }) => {
            if (node.level > this.maxHeadingLevel) {
              node.level = this.maxHeadingLevel
            }

            return h(
              `h${node.level}`,
              { key, class: `GenericStructuredText__heading t-h${node.level}` },
              children
            )
          }
        ),
        renderRule(
          isParagraph,
          ({ adapter: { renderNode: h }, children, key }) => {
            return h(
              'p',
              {
                key,
                class:
                  'GenericStructuredText__body' +
                  ' ' +
                  `t-${this.copySize}`
              },
              children
            )
          }
        ),
        renderRule(
          isLink,
          ({ adapter: { renderNode: h }, node, children, key }) => {
            return h(
              VisionLink,
              {
                key,
                lineLink: true,
                size: 'inherit',
                to: this.resolveLocalePath(node.url),
                display: 'inline-block'
              },
              children
            )
          }
        ),
      ]
    },
  },

  methods: {
    getInlineCtaConfig(record) {
      const buttonThemeConfig = {
        ...(record.buttonTheme?.buttonBackgroundGradient && {'--vision-button-bg-gradient': record.buttonTheme.buttonBackgroundGradient}),
        ...(record.buttonTheme?.buttonBackgroundColour?.hex && {'--vision-button-bg-colour': record.buttonTheme.buttonBackgroundColour?.hex}),
        ...(record.buttonTheme?.buttonTextColour?.hex && {'--vision-button-text-colour': record.buttonTheme.buttonTextColour.hex}),
        ...(record.buttonTheme?.buttonBackgroundColour?.hex && {'--vision-button-border-colour': record.buttonTheme.buttonBackgroundColour?.hex}),
        ...(record.buttonTheme?.buttonTextColour?.hex && {'--vision-button-text-colour': record.buttonTheme.buttonTextColour.hex})
      }

      switch (record.style) {
        case 'PrimaryButton':
          return {
            component: VisionButton,
            backgroundColour: 'gradient',
            outline: false,
            size: this.ctaSize,
            style: buttonThemeConfig
          }
        case 'SecondaryButton':
          return {
            component: VisionButton,
            textColour: 'black',
            outline: false,
            size: this.ctaSize,
            style: buttonThemeConfig
          }
        case 'TertiaryButton':
          return {
            component: VisionButton,
            backgroundColour: 'black',
            textColour: 'white',
            outline: false,
            size: this.ctaSize,
            style: buttonThemeConfig
          }
        case 'OutlineButton':
          return {
            component: VisionButton,
            backgroundColour: this.content.textColour === 'black' ? 'black' : 'white',
            textColour: this.content.textColour === 'black' ? 'black' : 'white',
            outline: true,
            size: this.ctaSize,
            style: buttonThemeConfig
          }
        case 'PrimaryLineLink':
          return {
            component: VisionLink,
            theme: 'gradient',
            lineLink: true
          }
        case 'SecondaryLineLink':
          return {
            component: VisionLink,
            lineLink: true
          }
      }
    },

    renderLinkToRecord({ record, children }) {
      if (record.__typename === 'SidebarPageRecord') {
        return h(
          VisionLink,
          {
            lineLink: true,
            size: 'inherit',
            onClick: (e) => {
              e.preventDefault()
              this.openSidebarPage(record.slug)
            },
          },
          () => children
        )
      }
    },
    renderBlock({ record }) {
      const isFirst =
        this.content?.copy?.value?.document?.children?.findIndex(
          (item) => item?.type === 'block' && item?.item === record.id
        ) === 0

      const widthSuffix = record.widthMode === 'percentage' ? '%' : 'px'

      switch (record.__typename) {
        case 'TitleBlockRecord': {
          return h(TitleBlock, {
            class: 'GenericStructuredText__title-block',
            title: {
              text: record.text,
              textSize: record.textSize,
              tag: record.tag,
              style: record.style,
            }
          })
        }

        case 'SingleImageRecord':
          return h(SingleImage, {
            class: { 'is-first': isFirst, 'is-block': true },
            image: record.image,
            maxWidth: `${record.maxWidth}${widthSuffix}`,
            minWidth: record.minWidth ? `${record.minWidth}${widthSuffix}` : undefined,
          })

        case 'HighlighterHeadingRecord':
          return h(HighlightHeading, {
            heading: record,
            background: 'light',
            headingSize: 'h3',
            size: record.size
          })

        case 'InlineCtaRecord': {
          const ctaConfig = this.getInlineCtaConfig(record)

          if (ctaConfig.component === VisionButton) {
            return h(
              ctaConfig.component,
              {
                backgroundColour: ctaConfig.backgroundColour,
                textColour: ctaConfig.textColour,
                square: false,
                outline: ctaConfig.outline,
                to: record.target,
                size: ctaConfig.size,
                promoButton: this.theme === 'promo',
                element: this.hasParentWrappingLink ? 'div' : '',
                style: ctaConfig.style,
                onClick: async (e) => {
                  this.$emit('button-clicked')

                  if (record.target?.startsWith('@')) {
                    this.$emit(record.target.replace('@', '')) // Emit the event name
                  }

                  if (record.target?.startsWith('@modal:')) {
                    e.preventDefault()

                    const modalId = record.target.split('@modal:')[1]
                    this.openModal({ slug: modalId })
                  }

                  if(record.trackingId) {
                    await this.$track.global.linkClicked({
                      linkText: record.copy,
                      interactionElement: record.trackingId,
                      interactionCategory: 'Generic Text',
                    })
                  }
                },
                class: 'GenericStructuredText__cta'
              },
              {
                content: () => record.copy,
              }
            )
          } else if (ctaConfig.component === VisionLink) {
            return h(
              ctaConfig.component,
              {
                theme: ctaConfig.theme,
                to: record.target,
                lineLink: true,
                display: 'inline-block',
                hasPromoTheme: this.theme === 'promo',
                onClick: async (e) => {
                  if (record.target?.startsWith('@')) {
                    this.$emit(record.target.replace('@', '')) // Emit the event name
                  }

                  if (record.target?.startsWith('@modal:')) {
                    e.preventDefault()

                    const modalId = record.target.split('@modal:')[1]
                    this.openModal({ slug: modalId })
                  }

                  if(record.trackingId) {
                    await this.$track.global.linkClicked({
                      linkText: record.copy,
                      interactionElement: record.trackingId,
                      interactionCategory: 'Generic Text',
                    })
                  }
                },
                class: 'GenericStructuredText__cta'
              },
              () => record.copy
            )
          }
          break;
        }

        case 'ArticleVideoRecord':
          return h(ArticleVideo, {
            class: 'GenericStructuredText__video',
            video: record.video,
            caption: record.caption,
            credit: record.credit,
          })

        default: {
          return null
        }
      }
    },
  },
}
</script>

<style lang="postcss">
.GenericStructuredText {
  @apply flex flex-col h-full w-full max-w-full;

  @screen md {
    &--block-align-north {
      @apply items-center justify-start;
    }

    &--block-align-northeast {
      @apply items-end justify-start;
    }

    &--block-align-east {
      @apply items-end justify-center;
    }

    &--block-align-southeast {
      @apply items-end justify-end;
    }

    &--block-align-south {
      @apply items-center justify-end;
    }

    &--block-align-southwest {
      @apply items-start justify-end;
    }

    &--block-align-west {
      @apply items-start justify-center;
    }

    &--block-align-northwest {
      @apply items-start justify-start;
    }

    &--block-align-centre {
      @apply items-center justify-center;
    }
  }

  @screen mdMax {
    &--mobile-block-align-north {
      @apply items-center justify-start;
    }

    &--mobile-block-align-northeast {
      @apply items-end justify-start;
    }

    &--mobile-block-align-east {
      @apply items-end justify-center;
    }

    &--mobile-block-align-southeast {
      @apply items-end justify-end;
    }

    &--mobile-block-align-south {
      @apply items-center justify-end;
    }

    &--mobile-block-align-southwest {
      @apply items-start justify-end;
    }

    &--mobile-block-align-west {
      @apply items-start justify-center;
    }

    &--mobile-block-align-northwest {
      @apply items-start justify-start;
    }

    &--mobile-block-align-centre {
      @apply items-center justify-center;
    }
  }

  ol {
    @apply mb-md;
    list-style-type: decimal;
    list-style-position: inside;

    li {
      @apply pb-xs;

      &:last-of-type {
        @apply pb-0;
      }

      p {
        padding-left: 4px;
        display: inline;
      }
    }
  }

  ul {
    @apply mb-md ml-4;

    list-style-type: disc;
    list-style-position: outside;

    li {
      @apply pb-xs;

      &:last-of-type {
        @apply pb-0;
      }

      ul {
        @apply pl-4 list-disc;
      }
    }
  }

  ul li p + ul {
    @apply -mt-sm;
  }

  h2, h3, h4, h5 {
    @apply mb-1 md:mb-2;
  }

  h2:not(.GenericStructuredText__title-block) {
    @apply mdMax:heading-3;
  }

  h3:not(.GenericStructuredText__title-block) {
    @apply mdMax:text-[11px];
  }

  p + p {
    @apply mt-xxs;
  }
}

.GenericStructuredText__heading {
  + .GenericStructuredText__cta {
    @apply mt-2;
  }
}

.GenericStructuredText__content {
  @apply flex flex-col justify-center w-full;

  &--text-black {
    @apply text-off-black;
  }

  &--text-white {
    @apply text-white;
  }

  @screen md {
    &--text-center {
      @apply text-center items-center;
    }

    &--text-left {
      @apply text-left items-start;
    }

    &--text-right {
      @apply text-right items-end;
    }
  }

  @screen mdMax {
    &--mobile-text-center {
      @apply text-center items-center;
    }

    &--mobile-text-left {
      @apply text-left items-start;
    }

    &--mobile-text-right {
      @apply text-right items-end;
    }
  }
}

.GenericStructuredText .SingleImage {
  @apply mb-0 pb-0 md:mb-2;
}

.GenericStructuredText__video {
  @apply w-full;
}

.GenericStructuredText {
  .GenericStructuredText__body + .GenericStructuredText__cta {
    @apply mt-xxs;
  }
}

.GenericStructuredText__content:has(.HighlightHeading--highlight-williamsfade) {
  container: GenericStructuredText / inline-size;
  width: 100%;
}

/* Promo Theme */
.GenericStructuredText--theme-promo {
  h1,h2,h3,h4,h5,h6 {
    @apply promo-t-gradient promo-t-gradient-animated;
  }
}
</style>
